import { useState } from "react";
import { useTranslation } from "../../context/TranslationContext";
import { getInstanceDisplayName, getInstanceVisibility, instanceNavId } from "../../utils/mintSpaceApps";
import Alert from "../ds/Alert";
import { AppScope } from "../ds/AppCover";
import AppIcon from "../ds/AppIcon";
import Button from "../ds/Button";
import Icon from "../ds/Icon";
import IconButton from "../ds/IconButton";
import Modal from "../ds/Modal";

/**
 * An app's copies in this space: open one, change its settings, or remove it. Figma's add
 * popup promises removal from Manage but draws no such screen, so this is built from the
 * design system. It's also the only way to remove an app without a mouse.
 */
const ManageAppModal = ({ app, copies, mintSpace, isDark, removing, onOpen, onSettings, onRemove, onClose }) => {
  const { t } = useTranslation();
  const [target, setTarget] = useState(null);
  if (!app) return null;
  const nameOf = (copy) => getInstanceDisplayName(mintSpace, copy, app.storeName);

  return (
    <AppScope app={app.id}>
      <Modal
        open
        title={t("appStorePage.manageTitle", { app: app.storeName })}
        description={t("appStorePage.manageHint")}
        closeLabel={t("common.close")}
        onClose={onClose}
        isDark={isDark}
      >
        <ul className="flex flex-col gap-space-8">
          {copies.map((copy) => (
            <li key={copy.instanceId} className="flex items-center gap-space-12 rounded-[22px] bg-bg-inset p-space-12">
              <AppIcon app={app.id} size={32} />
              <div className="flex min-w-0 flex-1 flex-col">
                <p className="truncate text-subheadline-semibold text-label-primary">{nameOf(copy)}</p>
                <p className="truncate text-footnote text-label-secondary">
                  {t(getInstanceVisibility(mintSpace, copy) === "private" ? "appStorePage.whoBuyers" : "appStorePage.whoMembers")}
                </p>
              </div>
              <Button variant="gray" size="s" onClick={() => onOpen(instanceNavId(copy))}>
                {t("appStorePage.open")}
              </Button>
              <IconButton label={t("appStorePage.settings")} variant="plain" onClick={() => onSettings(copy)}>
                <Icon name="edit" size={18} />
              </IconButton>
              <IconButton
                label={t("mintSpace.removeApp")}
                variant="plain"
                className="text-status-danger"
                onClick={() => setTarget(copy)}
              >
                <Icon name="trash" size={18} />
              </IconButton>
            </li>
          ))}
        </ul>
      </Modal>
      <Alert
        open={!!target}
        title={t("mintSpace.removeApp")}
        message={target ? t("mintSpace.confirmRemoveApp", { name: nameOf(target) }) : ""}
        confirmLabel={t("mintSpace.removeApp")}
        cancelLabel={t("common.cancel")}
        loading={removing}
        onConfirm={async () => {
          await onRemove(target);
          setTarget(null);
        }}
        onClose={() => setTarget(null)}
      />
    </AppScope>
  );
};

export default ManageAppModal;
